/**
 * 問診フォームデータを自然言語クエリに変換する
 */

import type {
  InquiryFormData,
  TargetAge,
  TargetGender,
  CategoryOption,
  DurationOption,
  MedicalVisitOption,
} from './types';

/**
 * 年代を日本語に変換するマッピング
 */
const TARGET_AGE_LABELS: Record<TargetAge, string> = {
  NOT_SELECTED: '年代を選択',
  INFANTS: '乳幼児',
  UNDER_10: '10歳未満',
  RANGE_10_19: '10代',
  RANGE_20_29: '20代',
  RANGE_30_39: '30代',
  RANGE_40_49: '40代',
  RANGE_50_59: '50代',
  RANGE_60_69: '60代',
  OVER_70: '70代以上',
};

/**
 * 性別を日本語に変換するマッピング
 */
const TARGET_GENDER_LABELS: Record<TargetGender, string> = {
  NOT_SELECTED: '性別を選択',
  F: '女性',
  M: '男性',
};

/**
 * カテゴリを日本語に変換するマッピング
 */
const CATEGORY_LABELS: Record<CategoryOption, string> = {
  should_go_hospital: '病院に行くべきか',
  which_department: '何科に行けばいいか',
  cause: '原因',
  how_to_deal: '対処法',
  medicine_treatment: '薬や治療法',
  other: 'その他のこと',
};

/**
 * 期間を日本語に変換するマッピング
 */
const DURATION_LABELS: Record<DurationOption, string> = {
  today: '今日初めて気づいた',
  yesterday: '昨日から',
  few_days: '2,3日以内',
  within_week: '1週間以内から',
  over_week: '1週間以上前から',
  no_symptom: '症状は無い',
};

/**
 * 受診状況を日本語に変換するマッピング
 */
const MEDICAL_VISIT_LABELS: Record<MedicalVisitOption, string> = {
  before: '受診前',
  during: '通院中',
  after: '受診後',
};

/**
 * 問診フォームデータを自然言語クエリに変換する
 *
 * @param formData 問診フォームデータ
 * @returns 自然言語形式の検索クエリ
 *
 * @example
 * // 返り値の例:
 * // "30代女性です。病院に行くべきかについて知りたいです。1週間以内から症状があります。受診前です。頭痛が続いています。"
 */
export function buildSearchQuery(formData: InquiryFormData): string {
  const parts: string[] = [];

  // キーワード（最初に追加）
  if (formData.keyword.trim()) {
    parts.push(`${formData.keyword}について。`);
  }

  // 年代・性別 (選択されているものだけ含める)
  if (
    formData.targetAge !== 'NOT_SELECTED' ||
    formData.targetGender !== 'NOT_SELECTED'
  ) {
    let demographicPart = '';

    // 年代が選択されている場合
    if (formData.targetAge !== 'NOT_SELECTED') {
      demographicPart += TARGET_AGE_LABELS[formData.targetAge];
    }

    // 性別が選択されている場合
    if (formData.targetGender !== 'NOT_SELECTED') {
      demographicPart += TARGET_GENDER_LABELS[formData.targetGender];
    }

    // 年代か性別のどちらかが選択されていればそれを出力
    if (demographicPart) {
      parts.push(`${demographicPart}です。`);
    }
  }

  // 知りたいこと（カテゴリ）
  if (formData.category) {
    const categoryLabel = CATEGORY_LABELS[formData.category];
    parts.push(`${categoryLabel}について知りたいです。`);
  }

  // 期間
  if (formData.duration) {
    const durationLabel = DURATION_LABELS[formData.duration];
    if (formData.duration === 'no_symptom') {
      parts.push(`${durationLabel}。`);
    } else {
      parts.push(`${durationLabel}症状があります。`);
    }
  }

  // 受診状況
  if (formData.medicalVisit) {
    const visitLabel = MEDICAL_VISIT_LABELS[formData.medicalVisit];
    parts.push(`${visitLabel}です。`);
  }

  // 自由入力
  if (formData.freeText.trim()) {
    parts.push(formData.freeText.trim());
  }

  const query = parts.join('');

  // クエリ長が1000文字を超える場合はトリミング（安全策）
  if (query.length > 1000) {
    return query.substring(0, 1000);
  }

  return query;
}
